import moment from "moment";
import {
  Title,
  Text,
  Group,
  Badge,
  Avatar,
  Card,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import React, { useState } from "react";
import _ from "lodash";
import {
  IconUser,
  IconBuildingBank,
  IconUsers,
  IconPhone,
  IconMail,
  IconStar,
  IconBuildingFactory2,
  IconBuildingFactory,
  IconBuildingSkyscraper,
  IconHome2,
  IconBrandProducthunt,
  IconBox,
  IconBuilding,
  IconFileText,
  IconMapPin,
} from "@tabler/icons";
import { useFormatter } from "./useFomatter";
import ReactJson from "react-json-view";
import { useTranslation } from "react-i18next";
import { McaApprovalListBadge } from "../components/mca/McaEntityIndicators";
import UserRefDisplay from "../components/userRefDisplay";
import {
  formatPhoneDisplay,
  isPhoneComplete,
  phoneToE164,
} from "../helpers/phoneUtils";
import { getDefaultPhone, getDefaultEmail } from "../helpers/clientContactUtils";
import { useTabNavigate } from "./useTabNavigate";
import { getEntityTabLabel } from "../utils/tabNavigation";

export const openWhatsapp = (phone, message = "") => {
  if (!phone) return;
  const number =
    typeof phone === "object"
      ? phoneToE164(phone)
      : phone.replace(/[^\w\s]/gi, "").replace(/ /g, "");

  if (!number) return;

  let url = `https://web.whatsapp.com/send?phone=${number}`;
  url += `&text=${encodeURI(message)}&app_absent=0`;

  window.open(url, "child-window");
};

export const useCellRender = () => {
  const [formatter] = useFormatter();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const theme = useMantineTheme();
  const secondaryLinkColor =
    theme.colorScheme === "dark" ? theme.colors.dark[2] : theme.colors.gray[7];
  const secondaryLinkHoverColor = theme.colors.blue[6];
  const FollowupAction = (col, data) => (
    <>
      <Group>
        <Badge>{data?.messages?.length ?? 0}</Badge>
        <span>
          <Text size="sm">{data.title}</Text>
          <Text color="dimmed" size="xs" transform="capitalize">
            {t(data.type)}
          </Text>
        </span>
      </Group>
    </>
  );

  const BidType = (type, size = "sm") => {
    const getColor = (type) => {
      switch (type) {
        case "COMMISSION":
          return "green";
        case "DEBT":
          return "red";
        case "ALLOWANCE":
          return "blue";

        default:
          return "dimmed";
      }
    };
    return (
      <>
        <Badge color={getColor(type)} size={size}>
          {t(type)}
        </Badge>
      </>
    );
  };

  const QuoRequest = {
    status: (status) => {
      const getStatusColor = (status) => {
        switch (status) {
          case "DFT":
            return "yellow";
          case "CAN":
            return "red";
          case "DONE":
            return "yellow";
          case "CONFIRM":
            return "green";
          case "COPIEDDN":
            return "blue";
        }
      };
      return (
        <>
          <Badge color={getStatusColor(status)} size="xs" radius={0}>
            {t(status)}
          </Badge>
        </>
      );
    },
  };

  const Status = (status, size = "xs", radius = 0) => {
    const getStatusColor = (status) => {
      const s = status?.toUpperCase();
      switch (s) {
        case "OPENING":
          return "red";
        case "HIDDEN":
          return "red";

        case "PENDING":
          return "red";
        case "DFT":
        case "DRAFT":
          return "yellow";
        case "PND":
          return "red";
        case "PAID":
          return "green";
        case "DONE":
          return "green";
        case "COMPLETED":
          return "green";
        case "CONFIRMED":
          return "green";
        case "CAN":
          return "red";
        case "INACTIVE":
          return "orange";
        case "PROSPECT":
          return "yellow";
        case "DORMANT":
          return "gray";
        case "SUSPENDED":
          return "red";
        case "TERMINATED":
          return "red";

        case "INPROGRESS":
          return "orange";

        case "TERMINATE":
          return "red";
        case "SKIPPED":
          return "orange";
        case "VERIFIED":
          return "green";
        case "VERIFING":
          return "yellow";
        default:
          return "blue";
      }
    };
    return (
      <Badge color={getStatusColor(status)} size={size} radius={radius}>
        {(() => {
          const s = String(status ?? "").toUpperCase();
          if (s === "DFT" || s === "DRAFT") return t("Draft");
          return t(status);
        })()}
      </Badge>
    );
  };

  const FollowupDuration = (start, end) => {
    return (
      <>
        {end && <Badge> {moment().diff(end, "day") + " Days"} </Badge>}
        {!end && <Badge> {moment().diff(start, "day") + " Days"} </Badge>}
        <Text>{moment(start).format("LLL")} </Text>
        {/* { end && <Text>{moment(end).format("LLL")} </Text> } */}
      </>
    );
  };

  const NumberFormat = (value) =>
    !Number.isNaN(parseFloat(value))
      ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "";

  const Currency = (price, currency) => (
    <span style={{ whiteSpace: "nowrap" }}>
      {formatter.currency(price, currency)}
    </span>
  );

  const Date = (date, format, fontWeight = "normal") => {
    if (!date) return "-";
    return moment(date).format(format);
  };

  const Mask = (
    value,
    markSymbol,
    showSymbolNumber,
    showValueNumber,
    maskPosition = "start",
  ) => {
    if (!value) return "";
    switch (maskPosition) {
      case "start":
        return (
          markSymbol.repeat(showSymbolNumber) + value.slice(-showValueNumber)
        );
      case "end":
        return (
          value.slice(0, showValueNumber) + markSymbol.repeat(showSymbolNumber)
        );
    }
  };

  const GetClientTypeIcon = (type) => {
    switch (type) {
      case "INDIVIDUAL":
        return <IconUser color="#7367F0" />;
      case "CORPORATE":
        return <IconBuildingBank color="#7367F0" />;

      case "COOPERATE":
        return <IconBuildingBank color="red" />;
      case "STAFF":
        return <IconUsers color="orange" />;
    }
  };

  const Address = (address, size = "md") => {
    try {
      const getAddressContent = (addPart) => {
        if (_.isEmpty(addPart) || _.isNaN(addPart) || _.isUndefined(addPart))
          return "";

        if (_.isString(addPart)) return addPart?.toUpperCase();

        if (_.isObject(addPart) && _.isArray(addPart.richText)) {
          const strarray = addPart.richText.map((p) =>
            _.isString(p.text) ? p.text?.toUpperCase() : "",
          );
          return strarray?.join(", ");
        }

        return "";
      };

      const street = [address?.line1, address?.line2]
        .map(getAddressContent)
        .filter(Boolean)
        .join(", ");
      const cityMatchesCountry =
        String(address?.city ?? "").trim().toLowerCase() ===
        String(address?.country ?? "").trim().toLowerCase();
      const locality = [cityMatchesCountry ? "" : address?.city, address?.state]
        .map(getAddressContent)
        .filter(Boolean)
        .join(", ");
      const region = [address?.zipCode ?? address?.zip, address?.country]
        .map(getAddressContent)
        .filter(Boolean)
        .join(", ");
      const lines = [street, locality, region].filter(Boolean);

      return (
        <>
          {address?.isDefault && (
            <Badge color="green" size="xs" radius="sm" mb={4}>
              Default
            </Badge>
          )}
          {lines.map((line, index) => (
            <Text key={index} size={size} lineClamp={2}>
              {line}
            </Text>
          ))}
          {address?.tags?.length > 0 && (
            <Group spacing={4} mt={4}>
              {address.tags.map((tag) => (
                <Badge key={tag} size="xs" variant="outline">
                  {tag}
                </Badge>
              ))}
            </Group>
          )}
        </>
      );
    } catch (error) {
      console.log(error);
      return "";
    }
  };

  const ADDRESS_TYPE_ICONS = {
    correspondence: IconMail,
    residental: IconHome2,
    registeredOffice: IconBuilding,
    contract: IconFileText,
    other: IconMapPin,
  };

  const normalizeAddressTypeKey = (type) => {
    const lowered = String(type ?? "").trim().toLowerCase();
    if (!lowered) return "other";
    const match = Object.keys(ADDRESS_TYPE_ICONS).find(
      (key) => key.toLowerCase() === lowered,
    );
    return match ?? lowered;
  };

  const AddressType = (type) => {
    const lookupKey = normalizeAddressTypeKey(type);
    const Icon = ADDRESS_TYPE_ICONS[lookupKey] ?? IconHome2;
    return (
      <Group spacing={6} noWrap align="center">
        <Icon size={14} stroke={1.5} />
        <Text size="xs" transform="capitalize" sx={{ whiteSpace: "nowrap" }}>
          {t(lookupKey)}
        </Text>
      </Group>
    );
  };

  const CONTACT_TYPE_ICONS = {
    work: IconBuilding,
    home: IconHome2,
    mobile: IconPhone,
    fax: IconFileText,
    personal: IconUser,
    billing: IconBuildingBank,
    other: IconMapPin,
  };

  const ContactType = (type) => {
    const lookupKey = String(type ?? "").toLowerCase();
    const Icon = CONTACT_TYPE_ICONS[lookupKey] ?? IconMapPin;
    return (
      <Group spacing={6} noWrap align="center">
        <Icon size={14} stroke={1.5} />
        <Text size="xs" transform="capitalize" sx={{ whiteSpace: "nowrap" }}>
          {t(lookupKey)}
        </Text>
      </Group>
    );
  };

  const ClientInfo = (
    client,
    showPhone = true,
    showEmail = false,
    showIcon = false,
    allowWhatsapp = true,
  ) => {
    const defaultPhone = getDefaultPhone(client);
    const defaultEmail = getDefaultEmail(client);
    return (
      <>
        {client && (
          <Group noWrap>
            {showIcon && <div>{GetClientTypeIcon(client.type)}</div>}
            <span>
              <Text>
                {_.startCase(_.toLower(client.name))} {client.cname}
              </Text>
              <Group spacing={"lg"} noWrap>
                {showPhone && defaultPhone && isPhoneComplete(defaultPhone) && (
                  <UnstyledButton onClick={() => openWhatsapp(defaultPhone)}>
                    <Group spacing={3} noWrap>
                      <IconPhone size={10}></IconPhone>
                      <Text size="xs" color={"dimmed"}>
                        {formatPhoneDisplay(defaultPhone)}
                      </Text>
                    </Group>
                  </UnstyledButton>
                )}

                {showEmail && defaultEmail && (
                  <Group spacing={3} noWrap>
                    <IconMail size={10} />
                    <Text size="xs" color={"dimmed"} lineClamp={1}>
                      {defaultEmail}
                    </Text>
                  </Group>
                )}
              </Group>
            </span>
          </Group>
        )}
      </>
    );
  };

  const ProductInfo = (
    product,
    textSize = "sm",
    particulars = [],
    { hideAvatar = false } = {},
  ) => {
    return (
      product && (
        <Group noWrap>
          {/* <ReactJson src={product} style={{ background: "white" }} /> */}
          {!hideAvatar && (
            <Avatar
              src={
                product.provider?.image
                  ? `${process.env.REACT_APP_FILE_URL}/${product.provider?.image}`
                  : null
              }
              radius={"xl"}
            >
              {(!product?.provider || !product.provider?.image) && <IconBox />}
            </Avatar>
          )}
          <span>
            <Text lineClamp={1} size={textSize}>
              {product.name}
            </Text>
            <Text color={"dimmed"} lineClamp={1} size={textSize}>
              {product.cname}
            </Text>
            {product.provider && (
              <Text color={"dimmed"} lineClamp={1} size={textSize - 2} italic>
                {product.provider.name}
              </Text>
            )}
            {!_.isEmpty(particulars) && (
              <Text color={"green"} lineClamp={1} size={textSize - 2} italic>
                {particulars
                  .filter((p) => p.isKey)
                  .map((p) => p.detail)
                  .join("/ ")}
              </Text>
            )}
          </span>
        </Group>
      )
    );
  };

  const ProductInfo2 = (
    { productType, provider, particulars = [] },
    textSize = "sm",
    { hideAvatar = false } = {},
  ) => {
    return (
      productType && (
        <Group noWrap>
          {/* <ReactJson src={particulars} style={{ background: "white" }} /> */}
          {!hideAvatar && (
            <Avatar
              src={
                provider?.image
                  ? `${process.env.REACT_APP_FILE_URL}/${provider?.image}`
                  : null
              }
              radius={"xl"}
            >
              {(!provider || !provider?.image) && <IconBox />}
            </Avatar>
          )}
          <span>
            <Text lineClamp={1} size={textSize}>
              {productType?.name}
            </Text>
            <Text color={"dimmed"} lineClamp={1} size={textSize}>
              {productType?.cname}
            </Text>
            {provider && (
              <Text color={"dimmed"} lineClamp={1} size={textSize - 2} italic>
                {provider.name}
              </Text>
            )}
            {!_.isEmpty(particulars) && (
              <Text color={"green"} lineClamp={2} size={textSize - 2} italic>
                {particulars
                  .filter((p) => p.isKey)
                  .map((p) => p.detail)
                  .join("/ ")}
              </Text>
            )}
          </span>
        </Group>
      )
    );
  };

  const ProductType = (type) => {
    return (
      <div>
        <Text lineClamp={1}>{type?.name}</Text>
        <Text color={"dimmed"} lineClamp={1}>
          {type?.cname}
        </Text>
      </div>
    );
  };
  const ProductCategory = (cat) => {
    return (
      <div>
        <Text>{cat?.name}</Text>
        <Text color={"dimmed"}>{cat?.cname}</Text>
      </div>
    );
  };

  const ProviderInfo = (provider) => {
    return (
      provider && (
        <Group noWrap>
          {/* {provider} */}
          {/* <ReactJson src={provider} style={{ background: "white" }} /> */}
          <Avatar
            src={
              provider.image
                ? `${process.env.REACT_APP_FILE_URL}/${provider?.image}`
                : null
            }
            radius={"xl"}
          >
            <IconBuildingSkyscraper size="1.5rem" />
          </Avatar>
          <span>
            <Text>{provider.shortName}</Text>
            <Text size="xs" color={"dimmed"}>
              {provider.name}
            </Text>

            <Text color={"dimmed"}>{provider.cname}</Text>
          </span>
        </Group>
      )
    );
  };

  const ProductCode = (product) => {
    return (
      <>
        <Text>{product.code}</Text>
        <Text color={"dimmed"}>{product.provider?.name}</Text>
      </>
    );
  };

  const BankTransactionStatus = (status) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "MATCHING":
          return "yellow";
        case "MATCHED":
          return "green";
        case "SKIP":
          return "red";
        default:
          return "blue";
      }
    };

    return (
      <Badge color={getStatusColor(status)} size="xs" radius={0}>
        {t(status)}
      </Badge>
    );
  };

  const AIRequestOutputs = (aiRequest) => {
    const outputs = aiRequest.outputs || [];

    const getUrl = (docType) => {
      switch (docType) {
        case "DEBIT_NOTE":
          return "/m/gi/debitNote";
        case "QUOTATION":
          return "/m/gi/quotation";
        case "RENEWAL":
          return "/m/gi/renewal";
        case "ENDORSEMENT":
          return "/m/gi/endorsement";
        default:
          return "/m/gi/debitNote";
      }
    };

    const getDocTypeColor = (docType) => {
      switch (docType) {
        case "DEBIT_NOTE":
          return "green";
        case "QUOTATION":
          return "blue";
        case "RENEWAL":
          return "orange";
        case "ENDORSEMENT":
          return "purple";
        default:
          return "gray";
      }
    };

    return (
      <div>
        {/* <Input.Wrapper label={label}></Input.Wrapper> */}
        <div>
          {outputs.map((output) => (
            <Badge
              key={output.id}
              color={getDocTypeColor(output.docType)}
              component="a"
              size="xs"
              radius={0}
              href={
                output.id
                  ? `${getUrl(output.docType)}?id=${output.id}&mode=edit`
                  : null
              }
              target="_blank"
              style={{ cursor: "pointer" }}
              mr={5}
            >
              {output.code}
            </Badge>
          ))}
          {aiRequest?.payload?.email?.subject && (
            <Text size={"xs"} mt={5} lineClamp={1}>
              {aiRequest?.payload?.email?.subject}
            </Text>
          )}
          {aiRequest?.description && (
            <Text size={"xs"} mt={5} lineClamp={1} color={"dimmed"} italic>
              {aiRequest?.description}
            </Text>
          )}
        </div>
      </div>
    );
  };

  const AIRequestCode = (aiRequest) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "PENDING":
          return "yellow";
        case "COMPLETED":
          return "green";
        case "ERROR":
          return "red";
        default:
          return "blue";
      }
    };
    return (
      <Group noWrap>
        <span>
          <Group noWrap spacing={1}>
            <Badge color={"blue"} size="xs" radius={0}>
              {aiRequest.type}
            </Badge>
            <Badge
              color={getStatusColor(aiRequest.status)}
              size="xs"
              radius={0}
            >
              {t(aiRequest.status)}
            </Badge>
          </Group>

          <Text size={"xs"}>{aiRequest.code}</Text>
          <Text color={"dimmed"} size="xs">
            {moment(aiRequest.createdAt).format("YYYY-MM-DD")}
          </Text>
        </span>
      </Group>
    );
  };

  const QuoteRequestCode = (dn) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "DFT":
          return "yellow";

        case "CAN":
          return "red";

        case "DONE":
          return "green";

        default:
          return "blue";
      }
    };

    return (
      <Group noWrap>
        <span>
          <Badge color={getStatusColor(dn.status)} size="xs" radius={0}>
            {t(dn.status)}
          </Badge>
          <Text size={"xs"}>{dn.code}</Text>
          <Text color={"dimmed"} size="xs">
            {moment(dn.createdAt).format("YYYY-MM-DD")}
          </Text>
          {/* <Text size={"xs"}>{order.policyNumber}</Text> */}
        </span>
      </Group>
    );
  };

  const CreditNote = {
    getStatusColor: (status) => {
      switch (status) {
        case "DFT":
          return "yellow";

        case "WAIT":
          return "orange";
        case "CAN":
          return "red";
        case "DELIVERED":
          return "green";
        case "SETTLED":
          return "blue";

        case "UNKNOWN":
          return "red";
      }
    },
    status: (status) => {
      return (
        <>
          <Badge color={CreditNote.getStatusColor(status)} size="xs" radius={0}>
            {t(status)}
          </Badge>
        </>
      );
    },

    code: (cr, showDate = true) => {
      return (
        <Group noWrap>
          <span>
            <Badge
              color={CreditNote.getStatusColor(cr.status)}
              size="xs"
              radius={0}
            >
              {t(cr.status)}
            </Badge>
            <Text size={"xs"}>{cr.code}</Text>
            {showDate && (
              <Text color={"dimmed"} size="xs">
                {moment(cr.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </Text>
            )}
          </span>
        </Group>
      );
    },
  };

  const DebitNotePaymentStatus = (status) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "DFT":
          return "yellow";
        case "WAIT":
          return "red";
        case "MATCHING":
          return "orange";
        case "DIRECT":
          return "teal";
        case "PARTIAL":
          return "orange";
        case "PAID":
          return "blue";
        case "SETTLED":
          return "green";
        case "EN_NO_PAYMENT":
          return "green";
      }
    };
    return (
      <Badge color={getStatusColor(status)} size="xs" radius={0}>
        {t(status)}
      </Badge>
    );
  };

  const QuotationCode = (quo, linkPath) => {
    const getStatusColor = (status) => {
      switch (status) {
        //["DFT", "WAIT", "CAN", "CONFIRM"];
        case "DFT":
          return "yellow";
        case "WAIT":
          return "red";
        case "CAN":
          return "blue";
        case "CONFIRM":
          return "green";
        default:
          return "blue";
      }
    };

    const id = quo?._id ?? quo?.id;

    return (
      <Group noWrap>
        <span>
          <Badge color={getStatusColor(quo.status)} size="xs" radius={0}>
            {t(quo.status)}
          </Badge>
          {linkPath && id ? (
            <Text
              component="span"
              size="xs"
              weight={500}
              style={{
                lineHeight: 1.2,
                color: secondaryLinkColor,
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(`${linkPath}?id=${id}&mode=edit`, "_blank");
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = secondaryLinkHoverColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = secondaryLinkColor;
              }}
            >
              {quo.code}
            </Text>
          ) : (
            <Text size={"xs"}>{quo.code}</Text>
          )}
          <Text color={"dimmed"} size="xs">
            {moment(quo.createdAt).format("YYYY-MM-DD")}
          </Text>
        </span>
      </Group>
    );
  };

  const EndorsementAction = (endorsement) => {
    const getColor = (action) => {
      switch (action) {
        case "DN":
          return "green";
        case "CN":
          return "red";
        case "REFUND":
          return "orange";
        default:
          return "blue";
      }
    };
    const getText = (endorsement) => {
      switch (endorsement.action) {
        case "DN":
          return "Debit Note  ";
        case "CN":
          return "Credit Note";
        case "REFUND":
          return "Refund";
        case "NONE":
          return "None";
        default:
          return "None";
      }
    };
    return (
      <span>
        {/* <Badge color={getColor(endorsement.action)} size="xs" radius={0}> */}
        {getText(endorsement)}
        {/* </Badge> */}
        {/* <Text size={"xs"}>{endorsement.code}</Text>
        <Text color={"dimmed"} size="xs">
          {moment(endorsement.date).format("YYYY-MM-DD")}
        </Text> */}
      </span>
    );
  };

  const GetDebitNoteTypeColor = (type) => {
    const getTypeColor = (type) => {
      switch (type) {
        case "NEW":
          return "green";
        case "RENEW":
          return "blue";
        case "CREDIT":
          return "red";
        case "ENDOSE":
          return "orange";
      }
    };
    return getTypeColor(type);
  };

  const DebitNoteType = (type) => {
    return (
      <Badge color={GetDebitNoteTypeColor(type ?? "NEW")} size="xs" radius={0}>
        {t(type)}
      </Badge>
    );
  };

  const DebitNoteCode = (
    dn,
    showDate = true,
    showLink = true,
    linkPath,
    openInNewTab = false,
  ) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "DFT":
          return "yellow";
        case "WAIT":
          return "red";
        case "HOLD":
          return "red";
        case "CONFIRMED":
          return "blue";
        case "SETTLED":
          return "blue";
        case "EXP":
          return "green";
        case "CAN":
          return "orange";
        default:
          return "blue";
      }
    };

    const getTypeText = (type) => {
      switch (type) {
        case "NEW":
          return "N";
        case "RENEW":
          return "R";
        case "ENDOSE":
          return "E";
        case "CREDIT":
          return "CR";
        default:
          return "N";
      }
    };

    const openDebitNote = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const label = getEntityTabLabel(dn, dn._id, "debitNote") || dn.code;
      if (tabNav.enabled) {
        tabNav.openDetail({
          pathname: linkPath,
          id: dn._id,
          mode: "edit",
          label,
        });
        return;
      }
      if (openInNewTab) {
        window.open(`${linkPath}?id=${dn._id}&mode=edit`, "_blank");
        return;
      }
      window.location.assign(`${linkPath}?id=${dn._id}&mode=edit`);
    };

    const linkStyle = {
      lineHeight: 1.2,
      color: secondaryLinkColor,
      cursor: "pointer",
      textDecoration: "none",
    };

    return (
      <Group noWrap style={{ minWidth: "100px" }}>
        <span>
          <Group noWrap spacing={4}>
            <Badge
              color={GetDebitNoteTypeColor(dn.type ?? "NEW")}
              size="xs"
              radius={0}
            >
              {getTypeText(dn.type)}
            </Badge>
            <Badge color={getStatusColor(dn.status)} size="xs" radius={0}>
              {t(dn.status)}
            </Badge>
            <McaApprovalListBadge entity={dn} />
          </Group>
          <Text size={"xs"} lineClamp={1}>
            {showLink && linkPath && (tabNav.enabled || openInNewTab) && (
              <Text
                component="span"
                size="xs"
                weight={500}
                style={linkStyle}
                onClick={openDebitNote}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = secondaryLinkHoverColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = secondaryLinkColor;
                }}
              >
                {dn.code}
              </Text>
            )}
            {showLink && linkPath && !tabNav.enabled && !openInNewTab && (
              <a
                style={{ textDecoration: "none" }}
                href={`${linkPath}?id=${dn._id}&mode=edit`}
              >
                {dn.code}
              </a>
            )}
            {!linkPath && dn.code}
          </Text>

          {showDate && (
            <Text color={"dimmed"} size="xs">
              {dn.date ? moment(dn.date).format("YYYY-MM-DD") : ""}
            </Text>
          )}
          {/* <Text size={"xs"}>{order.policyNumber}</Text> */}
        </span>
      </Group>
    );
  };

  const RenewalReceived = (rn) => {
    const receivedColor = (receivedRenewal) => {
      if (receivedRenewal && receivedRenewal.date) {
        return "blue";
      }
      return "orange";
    };
    return (
      <>
        <Badge color={receivedColor(rn.receivedRenewal)} size="xs" radius={0}>
          {rn.receivedRenewal ? "Received" : "Not Received"}
        </Badge>
        <Text color={"dimmed"} size="xs">
          {rn.receivedRenewal?.date
            ? moment(rn.receivedRenewal.date).format("YYYY-MM-DD")
            : ""}
        </Text>
      </>
    );
  };

  const RenewalCode = (rn) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "DFT":
          return "yellow";
        case "WAIT":
          return "orange";
        case "PENDING":
          return "yellow";
        case "CONFIRM":
          return "green";
        case "TERMINATE":
          return "red";
        default:
          return "blue";
      }
    };

    return (
      <Group noWrap>
        <span>
          <Group noWrap spacing={1}>
            <Badge
              color={rn.fromSystem ? "green" : "blue"}
              size="xs"
              radius={0}
            >
              {rn.fromSystem ? "System" : "Manual"}
            </Badge>
            <Badge color={getStatusColor(rn.status)} size="xs" radius={0}>
              {t(rn.status)}
            </Badge>
          </Group>

          <Text size={"xs"} lineClamp={1}>
            {rn.code}
          </Text>
          <Text color={"dimmed"} size="xs">
            {moment(rn.createdAt).format("YYYY-MM-DD")}
          </Text>
          {/* <Text size={"xs"}>{order.policyNumber}</Text> */}
        </span>
      </Group>
    );
  };

  const Order = {
    status: (status) => {
      const getStatusColor = (status) => {
        switch (status) {
          case "DFT":
            return "yellow";
          case "CAN":
            return "red";
          case "RCV":
            return "yellow";
          case "PND":
            return "orange";
          case "OTH":
            return "orange";
          case "AIP":
            return "cyan";
          case "SUB":
            return "cyan";
          case "INF":
            return "green";
          case "DONE":
            return "green";

          case "SUS":
            return "red";
          case "COL":
            return "red";
          case "TER":
            return "red";
          case "LAP":
            return "red";
          case "WTH":
            return "red";
          case "REJ":
            return "red";
          case "SUR":
            return "red";
          case "TRO":
            return "red";

          case "EXP":
            return "red";
          case "AIP_EXP":
            return "red";
          case "MAT":
            return "red";
          case "DEA":
            return "red";
          case "UNKNOWN":
            return "red";
        }
      };
      return (
        <>
          <Badge color={getStatusColor(status)} size="xs" radius={0}>
            {t(status)}
          </Badge>
        </>
      );
    },
    type: (type) => (
      <>
        {type === "NEW" ? (
          <Badge color="green" size="xs" radius={0} mr={1}>
            N
          </Badge>
        ) : (
          <Badge color="yellow" size="xs" radius={0} mr={1}>
            T
          </Badge>
        )}
      </>
    ),
    code: (order, bySignDate = false) => {
      return (
        <Group noWrap>
          <span>
            <Group spacing={4} noWrap>
              {order.type === "NEW" ? (
                <Badge color="green" size="xs" radius={0}>
                  N
                </Badge>
              ) : (
                <Badge color="yellow" size="xs" radius={0}>
                  T
                </Badge>
              )}
              {Order.status(order.status)}
              <McaApprovalListBadge entity={order} />
            </Group>
            <Text size={"xs"}>{order.code}</Text>
            <Text color={"dimmed"} size="xs" sx={{ whiteSpace: "nowrap" }}>
              {!bySignDate && moment(order.createdAt).format("YYYY-MM-DD")}
              {bySignDate &&
                order.signedDate &&
                moment(order.signedDate).format("YYYY-MM-DD")}
            </Text>
            {/* <Text size={"xs"}>{order.policyNumber}</Text> */}
          </span>
        </Group>
      );
    },

    serviceCode: (service) => {
      return (
        <Group>
          <span>
            <Group spacing={1}>
              {Order.status(service.status)}
              <Badge color="blue" size="xs" radius={0} mr={1}>
                {service.serviceType.name}
              </Badge>
            </Group>
            <Text size={"xs"}>{service.code}</Text>
            <Text color={"dimmed"} size="xs" sx={{ whiteSpace: "nowrap" }}>
              {moment(service.createdAt).format("YYYY-MM-DD")}
            </Text>
            {/* <Text size={"xs"}>{order.policyNumber}</Text> */}
          </span>
        </Group>
      );
    },

    simpleCodeWithStatus: (order, fontWeight = 600) => (
      <Group spacing={"lg"}>
        <Text weight={fontWeight}>
          {order.code} {Order.status(order.status)}
        </Text>
      </Group>
    ),
  };

  const Allowance = {
    name: (allowance) => (
      <div>
        <Text>{allowance.plan?.name}</Text>
        <Text>{allowance.plan?.cname}</Text>
      </div>
    ),
  };

  const Users = (
    users,
    maxDisplayUser = 4,
    showName = true,
    showAvatar = true,
    truncateNames = true,
  ) => {
    const [formatter] = useFormatter();

    const userExtra = _.isEmpty(users) ? 0 : users.length - maxDisplayUser;

    const getStatusColor = (status) => {
      switch (status) {
        case "INACTIVE":
          return "orange";
        case "TERMINATED":
          return "red";
        default:
          return "cyan";
      }
    };

    return (
      <>
        {/* {JSON.stringify(users)} */}

        <Group noWrap>
          {showAvatar && (
            <Avatar.Group spacing="sm" w={60}>
              {users?.map((user, index) =>
                index < maxDisplayUser ? (
                  <Avatar
                    key={index}
                    alt={user.name}
                    src={
                      user.image
                        ? `${process.env.REACT_APP_FILE_URL}/${user.image}`
                        : null
                    }
                    radius="xl"
                  >
                    {formatter.nameInitial(user)}
                  </Avatar>
                ) : null,
              )}
              {userExtra > 0 ? (
                <Avatar radius="xl">{`+${userExtra}`}</Avatar>
              ) : null}
            </Avatar.Group>
          )}

          <span>
            {showName && !_.isEmpty(users)
              ? users.map((user) => (
                  <>
                    <Group spacing={"xs"} noWrap align="flex-start">
                      <Badge
                        size="xs"
                        p={0}
                        radius={0}
                        mr={0}
                        w={50}
                        sx={{ flexShrink: 0 }}
                        color={getStatusColor(user.status)}
                      >
                        {user.staffId}
                      </Badge>
                      <Text
                        lineClamp={truncateNames ? 1 : undefined}
                        sx={
                          truncateNames
                            ? undefined
                            : {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                minWidth: 0,
                              }
                        }
                      >
                        {formatter.friendlyName(user)}
                      </Text>
                    </Group>
                  </>
                ))
              : null}
          </span>
        </Group>
      </>
    );
  };

  const UserAvatar = (user) => {
    return (
      <>
        <Avatar
          alt={user.name}
          src={
            _.isEmpty(user.image)
              ? null
              : `${process.env.REACT_APP_FILE_URL}/${user?.image}`
          }
          radius="xl"
        />
      </>
    );
  };

  const User = (user, showStaffId = true) => {
    const isPopulated =
      user &&
      typeof user === "object" &&
      (user.name || user.staffId || user.aliasName || user.cname);

    if (!user) return "-";

    if (typeof user === "string" || (user._id && !isPopulated)) {
      return <UserRefDisplay userRef={user} showStaffId={showStaffId} />;
    }

    const getStatusColor = (status) => {
      switch (status) {
        case "INACTIVE":
          return "orange";
        case "TERMINATED":
          return "red";
        default:
          return "cyan";
      }
    };

    return (
      <Group noWrap>
        <Avatar
          alt={user.name}
          src={
            _.isEmpty(user.image)
              ? null
              : `${process.env.REACT_APP_FILE_URL}/${user?.image}`
          }
          radius="xl"
        >
          {formatter.nameInitial(user)}
        </Avatar>
        <Group spacing={"xs"} noWrap>
          {showStaffId && user.staffId && (
            <Badge
              size="xs"
              p={0}
              radius={0}
              mr={0}
              w={50}
              color={getStatusColor(user.status)}
            >
              {user.staffId}
            </Badge>
          )}
          <Text lineClamp={1}>
            {formatter.friendlyName(user) || user.name || user.cname || "-"}
          </Text>
        </Group>
      </Group>
    );
  };

  const ConsultantClients = (user, clientNum = 0) => {
    return (
      <>
        {user && (
          <Group position="apart">
            <Group>
              <Avatar
                alt={user?.name}
                src={
                  user?.image
                    ? `${process.env.REACT_APP_FILE_URL}/${user?.image}`
                    : null
                }
                radius="xl"
              >
                {/* {user.name} {user.cname} */}
                {formatter.nameInitial(user?.name)}
              </Avatar>

              <div>
                <Text lineClamp={1}>{user?.name}</Text>
                <Text size={"xs"} color={"dimmed"}>
                  {user?.staffId}
                </Text>
              </div>
            </Group>
            <Badge size="xs">{clientNum} </Badge>
          </Group>
        )}
      </>
    );
  };

  const cellRender = {
    FollowupAction,
    FollowupDuration,
    ClientInfo,
    GetClientTypeIcon,
    ProductInfo,
    ProductInfo2,
    ProductCode,
    ProductType,
    ProductCategory,
    Status,
    Date,
    Users,
    User,
    Mask,
    UserAvatar,
    ConsultantClients,
    Allowance,
    Currency,
    NumberFormat,
    Order,
    Address,
    AddressType,
    ContactType,
    DebitNoteCode,
    EndorsementAction,
    BidType,
    ProviderInfo,
    QuoteRequestCode,
    BankTransactionStatus,
    QuoRequest,
    CreditNote,
    DebitNotePaymentStatus,
    RenewalCode,
    RenewalReceived,
    AIRequestCode,
    AIRequestOutputs,
    QuotationCode,
    DebitNoteType,
  };
  return [cellRender];
};
